import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  Dialog,
  DialogContent,
  IconButton,
  Backdrop,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CheckCircle,
  Person,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  Badge,
  Home,
  CreditCard,
  EmojiEvents,
  Verified,
  Close as CloseIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
  FamilyRestroom,
  Description,
  HealthAndSafety,
} from '@mui/icons-material';
import { athleteAPI } from '../api/athlete.api';
import { eventAPI } from '../api/event.api';
import { paymentAPI } from '../api/payment.api';
import { useAuth } from '../context/AuthContext';

const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#f8f9fa',
  padding: '2rem 0',
  [theme.breakpoints.down('md')]: {
    padding: '1rem 0',
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(41, 30, 108, 0.1)',
  marginBottom: theme.spacing(3),
  border: '1px solid #e0e0e0',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: 'rgba(41, 30, 108, 0.05)',
  borderLeft: `4px solid #291e6c`,
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
}));

const EventCard = styled(Card)(({ theme, selected }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  border: selected ? '3px solid #291e6c' : '2px solid #e0e0e0',
  backgroundColor: selected ? 'rgba(41, 30, 108, 0.05)' : 'white',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 6px 16px rgba(41, 30, 108, 0.2)',
    borderColor: '#291e6c',
  },
  [theme.breakpoints.down('md')]: {
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  },
}));

const ImagePreview = styled('img')(({ theme }) => ({
  width: '100%',
  maxWidth: '200px',
  height: 'auto',
  borderRadius: '8px',
  border: '2px solid #e0e0e0',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: '150px',
    '&:hover': {
      transform: 'scale(1.02)',
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: '12px 32px',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  fontFamily: "'Oswald', sans-serif",
  backgroundColor: '#291e6c',
  '&:hover': {
    backgroundColor: '#1f1750',
  },
  [theme.breakpoints.down('md')]: {
    padding: '10px 24px',
    fontSize: '0.9rem',
  },
}));

const ProcessingDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    padding: theme.spacing(4),
    textAlign: 'center',
    minWidth: '400px',
    backgroundColor: '#fff',
    boxShadow: '0 8px 32px rgba(41, 30, 108, 0.3)',
    [theme.breakpoints.down('md')]: {
      minWidth: '90vw',
      padding: theme.spacing(3),
      margin: theme.spacing(2),
    },
  },
}));

const InfoRow = ({ icon, label, value }) => (
  <TableRow>
    <TableCell sx={{ borderBottom: 'none', py: 1.5, color: '#666', fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
          {label}
        </Typography>
      </Box>
    </TableCell>
    <TableCell sx={{ borderBottom: 'none', py: 1.5, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
      <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>{value}</Typography>
    </TableCell>
  </TableRow>
);

const PaymentPage = () => {
    const { isAuthenticated, user, logout, loading: isLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [athleteData, setAthleteData] = useState(null);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [priceBreakdown, setPriceBreakdown] = useState(null);

  
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    loadData();
  }, []);

  
  if (isLoading) {
      return <div>Loading...</div>;
    }

  const loadData = async () => {
    try {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication required. Please login first.');
      navigate('/login');
      return;
    }
      const profileResponse = await athleteAPI.getProfile();
      if (profileResponse.success) {
        setAthleteData(profileResponse.data);

        if (profileResponse.data.selectedEvent) {
          setSelectedEvent(profileResponse.data.selectedEvent.eventId);
          calculateBreakdown(profileResponse.data.selectedEvent.eventAmount);
        }
      }

      const eventsResponse = await eventAPI.getActiveEvents();
      if (eventsResponse.success) {
        setAvailableEvents(eventsResponse.data || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(error.response?.data?.message || 'Failed to load data');
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate('/register');
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateBreakdown = (baseAmount) => {
    const gstAmount = baseAmount * 0.18;
    const convenienceAmount = baseAmount * 0.02;
    const totalAmount = baseAmount + gstAmount + convenienceAmount;

    setPriceBreakdown({
      baseAmount: baseAmount.toFixed(2),
      gstAmount: gstAmount.toFixed(2),
      gstRate: 18,
      convenienceAmount: convenienceAmount.toFixed(2),
      convenienceRate: 2,
      totalAmount: totalAmount.toFixed(2),
    });
  };

  const handleEventSelection = async (eventId) => {
    if (processing) return;

    try {
      setSelectedEvent(eventId);

      const event = availableEvents.find(e => e._id === eventId);
      if (event) {
        calculateBreakdown(event.amount);
      }

      toast.success('Event selected successfully');
    } catch (error) {
      console.error('Error selecting event:', error);
      toast.error('Failed to select event');
    }
  };

  const handleEditProfile = () => {
    navigate('/register', { state: { editMode: true, athleteData } });
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!selectedEvent) {
      toast.error('Please select an event before proceeding to payment');
      return;
    }

    setProcessing(true);

    try {
      await paymentAPI.selectEvent(athleteData._id, selectedEvent);

      const scriptLoaded = await loadRazorpayScript();

      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway. Please check your internet connection.');
        setProcessing(false);
        return;
      }

      const orderResponse = await paymentAPI.createOrder(athleteData._id);

      if (!orderResponse.success) {
        throw new Error(orderResponse.message || 'Failed to create order');
      }

      const {
        orderId,
        amount,
        currency,
        key,
        athleteName,
        athleteEmail,
        athletePhone,
        priceBreakdown: breakdown
      } = orderResponse.data;

      const selectedEventData = availableEvents.find(e => e._id === selectedEvent);

      const options = {
        key: key,
        amount: amount,
        currency: currency,
        name: 'Indian Weightlifting Federation',
        description: selectedEventData ? `${selectedEventData.name} - Registration` : 'Event Registration',
        order_id: orderId,
        prefill: {
          name: athleteName,
          email: athleteEmail,
          contact: athletePhone,
        },
        theme: {
          color: '#291e6c',
        },
        handler: async function (response) {
          try {
            const verifyResponse = await paymentAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              athleteId: athleteData._id,
            });

            if (verifyResponse.success) {
              toast.success('Payment successful! Your registration is complete.');
              localStorage.setItem('paymentCompleted', 'true');
              setTimeout(() => {
                navigate('/success');
              }, 1500);
            } else {
              toast.error('Payment verification failed. Please contact support.');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error(error.response?.data?.message || 'Payment verification failed');
          } finally {
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: function () {
            setProcessing(false);
            toast.info('Payment cancelled');
          },
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on('payment.failed', function (response) {
        console.error('Payment failed:', response.error);
        toast.error('Payment failed: ' + response.error.description);
        setProcessing(false);
      });

      razorpay.open();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.message || 'Payment initiation failed');
      setProcessing(false);
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('https://') || path.startsWith('http://')) return path;
    const baseUrl = 'https://register.iwlf.in/api';
    return baseUrl.replace('/api', path);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <PageWrapper>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <CircularProgress size={60} sx={{ color: '#291e6c' }} />
          </Box>
        </Container>
      </PageWrapper>
    );
  }

  if (!athleteData) {
    return (
      <PageWrapper>
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ mb: 3 }}>
            No athlete data found. Please register first.
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate('/register')}
            sx={{ mt: 2, backgroundColor: '#291e6c' }}
          >
            Go to Registration
          </Button>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
          <Typography
            variant="h3"
            sx={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 700,
              color: '#291e6c',
              mb: 1,
              fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            VERIFY & PAY
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Review your details, select an event, and complete payment
          </Typography>
        </Box>

        {/* Athlete ID */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: 2, 
          mb: 3 
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontFamily: "'Oswald', sans-serif", 
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}
          >
            Your Athlete ID
          </Typography>
          <Chip
            icon={<Verified />}
            label={athleteData._id}
            color="primary"
            sx={{ 
              fontFamily: "'Oswald', sans-serif", 
              fontWeight: 600, 
              px: { xs: 1.5, sm: 2 }, 
              py: { xs: 2, sm: 2.5 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          />
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEditProfile}
            sx={{
              borderColor: '#291e6c',
              color: '#291e6c',
              fontFamily: "'Oswald', sans-serif",
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '6px 16px', sm: '8px 22px' },
              '&:hover': {
                borderColor: '#1f1750',
                backgroundColor: 'rgba(41, 30, 108, 0.05)',
              },
            }}
          >
            Edit Profile
          </Button>
        </Box>

        <Grid container spacing={{ xs: 2, md: 3 }}>
          {/* Left Column - Athlete Details */}
          <Grid item xs={12} lg={7}>
            {/* Personal Information */}
            <StyledPaper>
              <SectionHeader>
                <Person sx={{ color: '#291e6c', fontSize: { xs: 24, md: 28 } }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontFamily: "'Oswald', sans-serif", 
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  Personal Information
                </Typography>
              </SectionHeader>

              {/* Profile Photo */}
              {athleteData.photo && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Profile Photo
                  </Typography>
                  <ImagePreview
                    src={getImageUrl(athleteData.photo.path)}
                    alt="Profile"
                    onClick={() => setImagePreview({ url: getImageUrl(athleteData.photo.path), title: 'Profile Photo' })}
                  />
                </Box>
              )}

              {/* Details Table */}
              <TableContainer>
                <Table>
                  <TableBody>
                    <InfoRow
                      icon={<Person sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                      label="Full Name"
                      value={`${athleteData.firstName} ${athleteData.middleName || ''} ${athleteData.surname}`.trim()}
                    />
                    <InfoRow
                      icon={<FamilyRestroom sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                      label="Father's Name"
                      value={`${athleteData.fatherFirstName} ${athleteData.fatherMiddleName || ''} ${athleteData.fatherSurname}`.trim()}
                    />
                    {athleteData.motherFirstName && (
                      <InfoRow
                        icon={<FamilyRestroom sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                        label="Mother's Name"
                        value={`${athleteData.motherFirstName} ${athleteData.motherMiddleName || ''} ${athleteData.motherSurname || ''}`.trim()}
                      />
                    )}
                    <InfoRow
                      icon={<CalendarToday sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                      label="Date of Birth"
                      value={formatDate(athleteData.birthDate)}
                    />
                    <InfoRow
                      icon={<Person sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                      label="Gender"
                      value={athleteData.gender.charAt(0).toUpperCase() + athleteData.gender.slice(1)}
                    />
                    <InfoRow
                      icon={<HealthAndSafety sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                      label="Blood Group"
                      value={athleteData.bloodGroup || 'Not specified'}
                    />
                    <InfoRow
                      icon={<Badge sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                      label="Registration Type"
                      value={athleteData.registrationType.charAt(0).toUpperCase() + athleteData.registrationType.slice(1)}
                    />
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledPaper>

            {/* Contact Information */}
            <StyledPaper>
              <SectionHeader>
                <Phone sx={{ color: '#291e6c', fontSize: { xs: 24, md: 28 } }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontFamily: "'Oswald', sans-serif", 
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  Contact Information
                </Typography>
              </SectionHeader>

              <TableContainer>
                <Table>
                  <TableBody>
                    <InfoRow
                      icon={<Email sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                      label="Email"
                      value={athleteData.email}
                    />
                    <InfoRow
                      icon={<Phone sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                      label="Phone Number"
                      value={athleteData.phoneNumber}
                    />
                    <InfoRow
                      icon={<Badge sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                      label="Aadhar Number"
                      value={athleteData.aadharCardNumber}
                    />
                    {athleteData.kheloIndiaId && (
                      <InfoRow
                        icon={<EmojiEvents sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                        label="Khelo India ID"
                        value={athleteData.kheloIndiaId}
                      />
                    )}
                    {athleteData.district && (
                      <InfoRow
                        icon={<LocationOn sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                        label="District"
                        value={athleteData.district}
                      />
                    )}
                    {athleteData.stateDepartment && (
                      <InfoRow
                        icon={<Home sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                        label="State Department"
                        value={athleteData.stateDepartment}
                      />
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledPaper>

            {/* Address */}
            <StyledPaper>
              <SectionHeader>
                <Home sx={{ color: '#291e6c', fontSize: { xs: 24, md: 28 } }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontFamily: "'Oswald', sans-serif", 
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  Address Details
                </Typography>
              </SectionHeader>

              <TableContainer>
                <Table>
                  <TableBody>
                    <InfoRow
                      icon={<LocationOn sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                      label="Address"
                      value={
                        <>
                          {athleteData.addressLine1}
                          {athleteData.addressLine2 && <><br />{athleteData.addressLine2}</>}
                        </>
                      }
                    />
                    <InfoRow
                      icon={<LocationOn sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                      label="City"
                      value={athleteData.city}
                    />
                    <InfoRow
                      icon={<LocationOn sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                      label="State"
                      value={athleteData.state}
                    />
                    <InfoRow
                      icon={<LocationOn sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                      label="Pincode"
                      value={athleteData.pincode}
                    />
                    <InfoRow
                      icon={<LocationOn sx={{ fontSize: { xs: 18, sm: 20 }, color: '#291e6c' }} />}
                      label="Country"
                      value={athleteData.country}
                    />
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledPaper>

            {/* Documents */}
            <StyledPaper>
              <SectionHeader>
                <Description sx={{ color: '#291e6c', fontSize: { xs: 24, md: 28 } }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontFamily: "'Oswald', sans-serif", 
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  Uploaded Documents
                </Typography>
              </SectionHeader>

              {/* Identity Proof */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Identity Proof: {athleteData.identityProof?.type}
                </Typography>
                <Grid container spacing={2}>
                  {athleteData.identityProof?.front && (
                    <Grid item xs={6} sm="auto">
                      <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                        Front Side
                      </Typography>
                      <ImagePreview
                        src={getImageUrl(athleteData.identityProof.front.path)}
                        alt="Identity Proof Front"
                        onClick={() => setImagePreview({
                          url: getImageUrl(athleteData.identityProof.front.path),
                          title: 'Identity Proof - Front'
                        })}
                      />
                    </Grid>
                  )}
                  {athleteData.identityProof?.back && (
                    <Grid item xs={6} sm="auto">
                      <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                        Back Side
                      </Typography>
                      <ImagePreview
                        src={getImageUrl(athleteData.identityProof.back.path)}
                        alt="Identity Proof Back"
                        onClick={() => setImagePreview({
                          url: getImageUrl(athleteData.identityProof.back.path),
                          title: 'Identity Proof - Back'
                        })}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Proof of Age */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Proof of Age: {athleteData.proofOfAge?.type}
                </Typography>
                <Grid container spacing={2}>
                  {athleteData.proofOfAge?.front && (
                    <Grid item xs={6} sm="auto">
                      <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                        Front Side
                      </Typography>
                      <ImagePreview
                        src={getImageUrl(athleteData.proofOfAge.front.path)}
                        alt="Proof of Age Front"
                        onClick={() => setImagePreview({
                          url: getImageUrl(athleteData.proofOfAge.front.path),
                          title: 'Proof of Age - Front'
                        })}
                      />
                    </Grid>
                  )}
                  {athleteData.proofOfAge?.back && (
                    <Grid item xs={6} sm="auto">
                      <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                        Back Side
                      </Typography>
                      <ImagePreview
                        src={getImageUrl(athleteData.proofOfAge.back.path)}
                        alt="Proof of Age Back"
                        onClick={() => setImagePreview({
                          url: getImageUrl(athleteData.proofOfAge.back.path),
                          title: 'Proof of Age - Back'
                        })}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Address Proof */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Address Proof: {athleteData.addressProof?.type}
                </Typography>
                <Grid container spacing={2}>
                  {athleteData.addressProof?.front && (
                    <Grid item xs={6} sm="auto">
                      <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                        Front Side
                      </Typography>
                      <ImagePreview
                        src={getImageUrl(athleteData.addressProof.front.path)}
                        alt="Address Proof Front"
                        onClick={() => setImagePreview({
                          url: getImageUrl(athleteData.addressProof.front.path),
                          title: 'Address Proof - Front'
                        })}
                      />
                    </Grid>
                  )}
                  {athleteData.addressProof?.back && (
                    <Grid item xs={6} sm="auto">
                      <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                        Back Side
                      </Typography>
                      <ImagePreview
                        src={getImageUrl(athleteData.addressProof.back.path)}
                        alt="Address Proof Back"
                        onClick={() => setImagePreview({
                          url: getImageUrl(athleteData.addressProof.back.path),
                          title: 'Address Proof - Back'
                        })}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Medical Certificate */}
              {athleteData.medicalCertificate && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Medical Certificate
                  </Typography>
                  <Chip
                    icon={<CheckCircle />}
                    label="Uploaded"
                    color="success"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              )}
            </StyledPaper>
          </Grid>

          {/* Right Column - Event Selection & Payment */}
          <Grid item xs={12} lg={5}>
            {/* Important Note */}
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              sx={{ 
                mb: { xs: 2, md: 3 }, 
                borderRadius: 2, 
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
              }}
            >
              Important: Once payment is completed, your profile cannot be edited. Please review all details carefully before proceeding.
            </Alert>

            {/* Event Selection */}
            <StyledPaper>
              <SectionHeader>
                <EmojiEvents sx={{ color: '#291e6c', fontSize: { xs: 24, md: 28 } }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontFamily: "'Oswald', sans-serif", 
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  Select Event
                </Typography>
              </SectionHeader>

              {availableEvents.length > 0 ? (
                <RadioGroup value={selectedEvent || ''} onChange={(e) => handleEventSelection(e.target.value)}>
                  {availableEvents.map((event) => (
                    <EventCard key={event._id} selected={selectedEvent === event._id} sx={{ mb: 2 }}>
                      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                        <FormControlLabel
                          value={event._id}
                          control={<Radio sx={{ color: '#291e6c', '&.Mui-checked': { color: '#291e6c' } }} />}
                          label={
                            <Box>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontFamily: "'Oswald', sans-serif", 
                                  fontWeight: 700, 
                                  color: '#291e6c',
                                  fontSize: { xs: '1rem', sm: '1.25rem' },
                                }}
                              >
                                {event.name}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#666', 
                                  mb: 1,
                                  fontSize: { xs: '0.875rem', sm: '1rem' },
                                }}
                              >
                                {event.description}
                              </Typography>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontFamily: "'Oswald', sans-serif", 
                                  fontWeight: 700, 
                                  color: '#291e6c',
                                  fontSize: { xs: '1rem', sm: '1.25rem' },
                                }}
                              >
                                ₹{event.amount?.toLocaleString('en-IN')}
                              </Typography>
                            </Box>
                          }
                          sx={{ width: '100%', margin: 0, alignItems: 'flex-start' }}
                        />
                      </CardContent>
                    </EventCard>
                  ))}
                </RadioGroup>
              ) : (
                <Alert severity="info" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  No events available at the moment. Please check back later.
                </Alert>
              )}
            </StyledPaper>

            {/* Payment Summary with Price Breakdown */}
            {selectedEvent && priceBreakdown && (
              <StyledPaper>
                <SectionHeader>
                  <CreditCard sx={{ color: '#291e6c', fontSize: { xs: 24, md: 28 } }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontFamily: "'Oswald', sans-serif", 
                      fontWeight: 700,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                    }}
                  >
                    Payment Summary
                  </Typography>
                </SectionHeader>

                {availableEvents.find(e => e._id === selectedEvent) && (
                  <>
                    {/* Event Details */}
                    <Box sx={{ mb: 3, p: { xs: 1.5, sm: 2 }, backgroundColor: 'rgba(41, 30, 108, 0.05)', borderRadius: 2 }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontFamily: "'Oswald', sans-serif", 
                          fontWeight: 700, 
                          color: '#291e6c', 
                          mb: 0.5,
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                        }}
                      >
                        {availableEvents.find(e => e._id === selectedEvent).name}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#666',
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                        }}
                      >
                        {availableEvents.find(e => e._id === selectedEvent).description}
                      </Typography>
                    </Box>

                    {/* Price Breakdown */}
                    <TableContainer>
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ borderBottom: 'none', py: 1, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>Event Price</TableCell>
                            <TableCell 
                              align="right" 
                              sx={{ 
                                borderBottom: 'none', 
                                py: 1, 
                                fontWeight: 600,
                                fontSize: { xs: '0.85rem', sm: '0.875rem' },
                              }}
                            >
                              ₹{priceBreakdown.baseAmount}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ borderBottom: 'none', py: 1, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>GST ({priceBreakdown.gstRate}%)</TableCell>
                            <TableCell 
                              align="right" 
                              sx={{ 
                                borderBottom: 'none', 
                                py: 1, 
                                fontWeight: 600,
                                fontSize: { xs: '0.85rem', sm: '0.875rem' },
                              }}
                            >
                              ₹{priceBreakdown.gstAmount}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ borderBottom: 'none', py: 1, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                              Convenience Charges ({priceBreakdown.convenienceRate}%)
                            </TableCell>
                            <TableCell 
                              align="right" 
                              sx={{ 
                                borderBottom: 'none', 
                                py: 1, 
                                fontWeight: 600,
                                fontSize: { xs: '0.85rem', sm: '0.875rem' },
                              }}
                            >
                              ₹{priceBreakdown.convenienceAmount}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={2} sx={{ py: 1 }}>
                              <Divider />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell 
                              sx={{ 
                                borderBottom: 'none', 
                                py: 1, 
                                fontSize: { xs: '1rem', sm: '1.1rem' }, 
                                fontWeight: 700 
                              }}
                            >
                              Total Amount
                            </TableCell>
                            <TableCell 
                              align="right" 
                              sx={{ 
                                borderBottom: 'none', 
                                py: 1, 
                                fontSize: { xs: '1rem', sm: '1.1rem' }, 
                                fontWeight: 700, 
                                color: '#291e6c' 
                              }}
                            >
                              ₹{priceBreakdown.totalAmount}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Alert 
                      icon={<ReceiptIcon />} 
                      severity="info" 
                      sx={{ 
                        mb: 3, 
                        fontSize: { xs: '0.75rem', sm: '0.85rem' } 
                      }}
                    >
                      • GST and Convenience charges are included in the total amount<br />
                      • Invoice will be sent to your email after successful payment
                    </Alert>

                    <StyledButton
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={handlePayment}
                      disabled={processing}
                      startIcon={processing ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <CreditCard />}
                    >
                      {processing ? 'Processing...' : `Pay ₹${priceBreakdown.totalAmount}`}
                    </StyledButton>

                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'block', 
                        textAlign: 'center', 
                        mt: 1, 
                        color: '#666',
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      }}
                    >
                      Secured payment powered by Razorpay
                    </Typography>
                  </>
                )}
              </StyledPaper>
            )}

            {!selectedEvent && (
              <Alert severity="info" sx={{ mt: { xs: 2, md: 3 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Please select an event to proceed with payment
              </Alert>
            )}
          </Grid>
        </Grid>

        {/* Payment Processing Modal */}
       <ProcessingDialog
  open={processing}
  disableEscapeKeyDown
  maxWidth="sm"
>
  <DialogContent sx={{ textAlign: 'center', py: { xs: 3, md: 4 } }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
      <CircularProgress
        size={80}
        thickness={4}
        sx={{ 
          color: '#291e6c',
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
          }
        }}
      />
    </Box>
    <Typography
      variant="h5"
      sx={{
        fontFamily: "'Oswald', sans-serif",
        fontWeight: 700,
        color: '#291e6c',
        mb: 2,
        fontSize: { xs: '1.25rem', sm: '1.5rem' },
      }}
    >
      Processing Payment
    </Typography>
    <Typography 
      variant="body1" 
      sx={{ 
        color: '#666', 
        mb: 1,
        fontSize: { xs: '0.875rem', sm: '1rem' },
      }}
    >
      Please wait while we process your payment
    </Typography>
    <Typography 
      variant="body2" 
      sx={{ 
        color: '#999',
        fontSize: { xs: '0.75rem', sm: '0.875rem' },
      }}
    >
      Do not close this window or press the back button
    </Typography>
  </DialogContent>
</ProcessingDialog>

        {/* Image Preview Dialog */}
        <Dialog
          open={!!imagePreview}
          onClose={() => setImagePreview(null)}
          maxWidth="md"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 2, sm: 4 },
              maxHeight: { xs: '90vh', sm: 'calc(100% - 64px)' },
            },
          }}
        >
          <IconButton
            onClick={() => setImagePreview(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              zIndex: 1,
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          {imagePreview && (
            <>
              <Typography 
                variant="h6" 
                sx={{ 
                  p: 2, 
                  fontFamily: "'Oswald', sans-serif", 
                  fontWeight: 700,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                }}
              >
                {imagePreview.title}
              </Typography>
              <DialogContent sx={{ p: { xs: 1, sm: 3 } }}>
                <img
                  src={imagePreview.url}
                  alt={imagePreview.title}
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </DialogContent>
            </>
          )}
        </Dialog>
      </Container>
    </PageWrapper>
  );
};

export default PaymentPage;
